import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  Tooltip,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Link,
  FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Warning as WarningIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import DVLALicenseRequestDialog from './DVLALicenseRequestDialog';

interface StaffMember {
  id: string;
  first_name: string;
  last_name: string;
  driving_license_number: string;
  date_of_birth: string;
  status: string;
  points_total: number;
  last_checked: string;
  license_valid_to: string;
  photocard_expiry: string;
  endorsement_count?: number;
  endorsement_summary?: string | null;
  common_entitlement_expiry: string;
  entitlement_dates_mixed: number;
  archived: boolean;
}

interface DVLAStaffTableProps {
  onRefresh?: () => void;
}

function isValidDate(dateString?: string | null) {
  if (!dateString) return false;
  return !isNaN(new Date(dateString).getTime());
}

function isDateExpired(dateString?: string | null) {
  if (!isValidDate(dateString)) return false;
  const date = new Date(dateString as string);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
}

function isDateExpiringSoon(dateString?: string | null, days = 90) {
  if (!isValidDate(dateString)) return false;
  const date = new Date(dateString as string);
  const today = new Date();
  const threshold = new Date();
  today.setHours(0, 0, 0, 0);
  threshold.setHours(0, 0, 0, 0);
  threshold.setDate(threshold.getDate() + days);
  date.setHours(0, 0, 0, 0);
  return date >= today && date <= threshold;
}

function hasDateAttention(dateString?: string | null, days = 90) {
  return isDateExpired(dateString) || isDateExpiringSoon(dateString, days);
}

const DVLAStaffTable: React.FC<DVLAStaffTableProps> = ({ onRefresh }) => {
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newLicenseNumber, setNewLicenseNumber] = useState('');
  const [addingStaff, setAddingStaff] = useState(false);
  const [addStaffConsentConfirmed, setAddStaffConsentConfirmed] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [checkConfirmDialogOpen, setCheckConfirmDialogOpen] = useState(false);
  const [pendingStaffId, setPendingStaffId] = useState<string | null>(null);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [cancelConfirmDialogOpen, setCancelConfirmDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLicenseText, setImportLicenseText] = useState('');
  const [importConsentConfirmed, setImportConsentConfirmed] = useState(false);
  const [importRunning, setImportRunning] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<Array<{ license: string; status: 'added' | 'duplicate' | 'error'; message?: string }>>([]);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  type SortKey = 'name' | 'age' | 'status' | 'points' | 'endorsements' | 'last_checked' | 'photocard_expiry' | 'can_drive_until';
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const sortDefaultDirection: Record<SortKey, 'asc' | 'desc'> = {
    name: 'asc',
    age: 'desc',
    status: 'asc',
    points: 'desc',
    endorsements: 'desc',
    last_checked: 'desc',
    photocard_expiry: 'asc',
    can_drive_until: 'asc',
  };

  const handleSort = (column: SortKey) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection(sortDefaultDirection[column]);
    }
  };

  const sortedStaff = useMemo(() => {
    if (!sortBy) return filteredStaff;
    return [...filteredStaff].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'name': {
          const na = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
          const nb = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
          cmp = na.localeCompare(nb);
          break;
        }
        case 'age': {
          // Newer DOB = younger. Ascending age = youngest first = newest DOB first.
          const da = a.date_of_birth ? new Date(a.date_of_birth).getTime() : 0;
          const db = b.date_of_birth ? new Date(b.date_of_birth).getTime() : 0;
          cmp = db - da;
          break;
        }
        case 'status':
          cmp = (a.status || '').toLowerCase().localeCompare((b.status || '').toLowerCase());
          break;
        case 'points':
          cmp = (a.points_total || 0) - (b.points_total || 0);
          break;
        case 'endorsements':
          cmp = (a.endorsement_count || 0) - (b.endorsement_count || 0);
          break;
        case 'last_checked': {
          const da = a.last_checked ? new Date(a.last_checked).getTime() : 0;
          const db = b.last_checked ? new Date(b.last_checked).getTime() : 0;
          cmp = da - db;
          break;
        }
        case 'photocard_expiry': {
          const da = a.photocard_expiry ? new Date(a.photocard_expiry).getTime() : Infinity;
          const db = b.photocard_expiry ? new Date(b.photocard_expiry).getTime() : Infinity;
          cmp = da - db;
          break;
        }
        case 'can_drive_until': {
          // Null expiry dates sort to the end
          const da = a.common_entitlement_expiry ? new Date(a.common_entitlement_expiry).getTime() : Infinity;
          const db = b.common_entitlement_expiry ? new Date(b.common_entitlement_expiry).getTime() : Infinity;
          cmp = da - db;
          break;
        }
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredStaff, sortBy, sortDirection]);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const [staffResponse, requestsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/dvla-staff/staff`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/dvla-license-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStaffList(staffResponse.data || []);
      setPendingRequests(requestsResponse.data || []);
    } catch (err: any) {
      console.error('Error fetching staff:', err);
      setError(err.response?.data?.error || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    let filtered = staffList.filter(staff => {
      if (staff.archived) return false;
      
      const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase();
      const licenseNumber = (staff.driving_license_number || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = fullName.includes(searchLower) || licenseNumber.includes(searchLower);
      if (!matchesSearch) return false;

      switch (filterBy) {
        case 'expiringSoon':
          if (!staff.license_valid_to) return false;
          const expiryDate = new Date(staff.license_valid_to);
          const thirtyDaysFromNow = new Date();
          thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
          return expiryDate <= thirtyDaysFromNow && expiryDate >= new Date();
          
        case 'expired':
          if (!staff.license_valid_to) return false;
          return new Date(staff.license_valid_to) < new Date();
          
        case 'highPoints':
          return (staff.points_total || 0) >= 6;

        case 'photocardIssues':
          return hasDateAttention(staff.photocard_expiry);

        case 'endorsements':
          return (staff.endorsement_count || 0) > 0;
          
        default:
          return true;
      }
    });

    setFilteredStaff(filtered);
  }, [staffList, searchTerm, filterBy]);

  const handleCheckLicense = (staffId: string) => {
    setPendingStaffId(staffId);
    setConsentConfirmed(false);
    setCheckConfirmDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!requestToCancel) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/dvla-license-requests/${requestToCancel}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCancelConfirmDialogOpen(false);
      setRequestToCancel(null);
      fetchStaff();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to cancel request');
    }
  };

  const handleConfirmCheckLicense = async () => {
    if (!consentConfirmed || !pendingStaffId) return;
    
    try {
      setCheckConfirmDialogOpen(false);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/dvla-staff/staff/${pendingStaffId}/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchStaff();
      if (onRefresh) onRefresh();
      setPendingStaffId(null);
      setConsentConfirmed(false);
    } catch (err: any) {
      console.error('Error checking license:', err);
      alert(err.response?.data?.error || 'Failed to check license');
      setPendingStaffId(null);
      setConsentConfirmed(false);
    }
  };

  const handleArchiveStaff = async (staffId: string) => {
    if (!window.confirm('Are you sure you want to archive this staff member?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/dvla-staff/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStaff();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      console.error('Error archiving staff:', err);
      alert(err.response?.data?.error || 'Failed to archive staff');
    }
  };

  const handleAddStaff = async () => {
    if (!newLicenseNumber || newLicenseNumber.length !== 16) {
      alert('Please enter a valid 16-character driving license number');
      return;
    }

    if (!addStaffConsentConfirmed) {
      alert('Please confirm that you have received the signed D906/ADD form(s) before adding a staff member');
      return;
    }

    setAddingStaff(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/dvla-staff/staff/lookup`,
        { drivingLicenceNumber: newLicenseNumber.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddDialogOpen(false);
      setNewLicenseNumber('');
      setAddStaffConsentConfirmed(false);
      fetchStaff();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      console.error('Error adding staff:', err);
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to add staff');
    } finally {
      setAddingStaff(false);
    }
  };

  const parseImportLicenses = (text: string): string[] => {
    const lines = text.split(/\r?\n/).map(line => line.replace(/\s+/g, '').toUpperCase().trim()).filter(Boolean);
    const valid: string[] = [];
    const seen = new Set<string>();
    for (const line of lines) {
      if (line.length !== 16) continue;
      if (seen.has(line)) continue;
      seen.add(line);
      valid.push(line);
    }
    return valid;
  };

  const extractLicensesFromFileContent = (content: string, fileName: string): string => {
    const isCsv = /\.csv$/i.test(fileName);
    const lines = content.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const licenses: string[] = [];
    for (const line of lines) {
      if (isCsv) {
        const cells = line.split(',').map(c => c.replace(/^"|"$/g, '').trim());
        for (const cell of cells) {
          const normalized = cell.replace(/\s+/g, '').toUpperCase();
          if (normalized.length === 16) {
            licenses.push(normalized);
            break;
          }
        }
      } else {
        const normalized = line.replace(/\s+/g, '').toUpperCase();
        if (normalized.length === 16) licenses.push(normalized);
      }
    }
    return Array.from(new Set(licenses)).join('\n');
  };

  const handleImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileName = file.name || '';
    if (!/\.(txt|csv)$/i.test(fileName)) {
      alert('Please select a .txt or .csv file.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = (reader.result as string) || '';
      const licensesText = extractLicensesFromFileContent(text, fileName);
      setImportLicenseText(licensesText);
    };
    reader.readAsText(file, 'UTF-8');
    event.target.value = '';
  };

  const handleImport = async () => {
    const licenses = parseImportLicenses(importLicenseText);
    if (licenses.length === 0) {
      alert('Please enter at least one valid 16-character driving license number (one per line).');
      return;
    }
    if (!importConsentConfirmed) {
      alert('Please confirm that you have received the signed D906/ADD form(s) before importing.');
      return;
    }
    setImportRunning(true);
    setImportProgress({ current: 0, total: licenses.length });
    setImportResults([]);
    const token = localStorage.getItem('token');
    const results: Array<{ license: string; status: 'added' | 'duplicate' | 'error'; message?: string }> = [];
    for (let i = 0; i < licenses.length; i++) {
      setImportProgress({ current: i + 1, total: licenses.length });
      try {
        await axios.post(
          `${API_BASE_URL}/dvla-staff/staff/lookup`,
          { drivingLicenceNumber: licenses[i] },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        results.push({ license: licenses[i], status: 'added' });
      } catch (err: any) {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Check failed';
        if (err.response?.status === 400 && err.response?.data?.error === 'Driver already exists') {
          results.push({ license: licenses[i], status: 'duplicate', message: msg });
        } else {
          results.push({ license: licenses[i], status: 'error', message: msg });
        }
      }
      setImportResults([...results]);
    }
    setImportRunning(false);
    fetchStaff();
    if (onRefresh) onRefresh();
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dvla-staff/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      if (!data || data.length === 0) {
        alert('No data available to export');
        return;
      }

      const headers = [
        'Staff ID',
        'First Name',
        'Last Name',
        'License Number',
        'Created Date',
        'Check ID',
        'Check Date',
        'Photocard Expiry',
        'Can Drive Until',
        'Points',
        'Status'
      ];

      let csvContent = headers.join(',') + '\n';
      
      data.forEach((row: any) => {
        const csvRow = [
          row.staff_id,
          escapeCsvValue(row.first_name || ''),
          escapeCsvValue(row.last_name || ''),
          escapeCsvValue(row.driving_license_number || ''),
          formatDateForExport(row.created_at),
          row.check_id || '',
          formatDateForExport(row.check_date),
          formatDateForExport(row.photocard_expiry || row.license_valid_to),
          formatDateForExport(row.common_entitlement_expiry),
          row.points_total || 0,
          escapeCsvValue(row.status || '')
        ];
        csvContent += csvRow.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date();
      const formattedDate = date.toISOString().split('T')[0];
      link.download = `staff_export_${formattedDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error exporting data:', err);
      alert('Failed to export data');
    }
  };

  const escapeCsvValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const formatDateForExport = (dateString: any): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return '';
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status?.toUpperCase()) {
      case 'VALID':
        return 'success';
      case 'EXPIRED':
        return 'warning';
      case 'SUSPENDED':
      case 'REVOKED':
      case 'DISQUALIFIED':
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateAge = (dateOfBirth: string): number => {
    if (!dateOfBirth) return 0;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const parseEndorsementSummary = (summary?: string | null): Array<{ code: string; description: string }> => {
    if (!summary) return [];

    return summary
      .split('|||')
      .map((item) => {
        const [code = '', ...descriptionParts] = item.split(':::');
        return {
          code: code.trim(),
          description: descriptionParts.join(':::').trim()
        };
      })
      .filter(item => item.code || item.description);
  };

  const formatEndorsementSummary = (summary?: string | null) => {
    const items = parseEndorsementSummary(summary);
    if (items.length === 0) return 'No endorsements';

    return items
      .map(item => item.description ? `${item.code} - ${item.description}` : item.code)
      .join(', ');
  };

  const getDateWarningSx = (dateString?: string | null) =>
    hasDateAttention(dateString)
      ? {
          color: '#ED6C02',
          fontWeight: 700,
          backgroundColor: 'rgba(237, 108, 2, 0.14)',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          display: 'inline-block'
        }
      : undefined;

  const handleViewDetails = async (staffId: string) => {
    setLoadingDetails(true);
    setDetailDialogOpen(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dvla-staff/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Staff details response:', response.data);
      console.log('Vehicle categories:', response.data.vehicleCategories);
      setSelectedStaff(response.data);
    } catch (err: any) {
      console.error('Error fetching staff details:', err);
      alert(err.response?.data?.error || 'Failed to fetch staff details');
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
          <TextField
            placeholder="Search by name or license number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterBy}
              label="Filter"
              onChange={(e) => setFilterBy(e.target.value)}
            >
              <MenuItem value="all">All Staff</MenuItem>
              <MenuItem value="expiringSoon">Expiring Soon (30 days)</MenuItem>
              <MenuItem value="expired">Expired Licenses</MenuItem>
              <MenuItem value="photocardIssues">Photocard Expiry Issues (90 days)</MenuItem>
              <MenuItem value="endorsements">Has Endorsements</MenuItem>
              <MenuItem value="highPoints">High Points (6+)</MenuItem>
              <MenuItem value="pending">Pending Requests ({pendingRequests.length})</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchStaff}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<EmailIcon />}
            onClick={() => setRequestDialogOpen(true)}
          >
            Request License Check
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import Licenses
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Staff
          </Button>
        </Box>
      </Box>

      {/* Pending Requests Section - Only show on Pending filter */}
      {filterBy === 'pending' && !loading && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HourglassEmptyIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">
                Pending License Check Requests ({pendingRequests.length})
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              These requests are awaiting submission from the recipients. Credits will be deducted when they submit their details.
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRequests.map((request) => {
                    const recipientName = request.recipient_first_name && request.recipient_last_name
                      ? `${request.recipient_first_name} ${request.recipient_last_name}`
                      : request.recipient_first_name || request.recipient_last_name || '-';
                    
                    const expiresAt = new Date(request.expires_at);
                    const now = new Date();
                    const hoursUntilExpiry = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60);
                    const isExpiringSoon = hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
                    
                    const formatDate = (dateString: string) => {
                      return new Date(dateString).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      });
                    };

                    const handleResend = async (requestId: string) => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(
                          `${API_BASE_URL}/dvla-license-requests/${requestId}/resend`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        alert('Request email resent successfully');
                      } catch (err: any) {
                        alert(err.response?.data?.message || 'Failed to resend request');
                      }
                    };

                    const handleCancel = (requestId: string) => {
                      setRequestToCancel(requestId);
                      setCancelConfirmDialogOpen(true);
                    };

                    return (
                      <TableRow key={request.id}>
                        <TableCell>{recipientName}</TableCell>
                        <TableCell>{request.recipient_email}</TableCell>
                        <TableCell>{formatDate(request.requested_at)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isExpiringSoon && (
                              <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            )}
                            <Typography
                              variant="body2"
                              color={isExpiringSoon ? 'warning.main' : 'text.primary'}
                            >
                              {formatDate(request.expires_at)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="Resend Email">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleResend(request.id)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancel(request.id)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Empty state for pending filter */}
      {filterBy === 'pending' && !loading && pendingRequests.length === 0 && (
        <Alert severity="info">No pending license check requests</Alert>
      )}

      {/* Staff Table - Hide when showing pending filter */}
      {filterBy !== 'pending' && (
        <>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : filteredStaff.length === 0 ? (
            <Alert severity="info">No staff records found</Alert>
          ) : (
        <TableContainer component={Paper}>
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Click any underlined column header to sort
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 700 } }}>
                <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                  <Tooltip title="Sort by name" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : sortDefaultDirection['name']}
                      onClick={() => handleSort('name')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'name' ? 1 : 0.3 } }}
                    >
                      Name
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell>License Number</TableCell>
                <TableCell align="center" sortDirection={sortBy === 'age' ? sortDirection : false}>
                  <Tooltip title="Sort by age" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'age'}
                      direction={sortBy === 'age' ? sortDirection : sortDefaultDirection['age']}
                      onClick={() => handleSort('age')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'age' ? 1 : 0.3 } }}
                    >
                      Age
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sortDirection={sortBy === 'status' ? sortDirection : false}>
                  <Tooltip title="Sort by status" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : sortDefaultDirection['status']}
                      onClick={() => handleSort('status')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'status' ? 1 : 0.3 } }}
                    >
                      Status
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sortDirection={sortBy === 'points' ? sortDirection : false}>
                  <Tooltip title="Sort by penalty points" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'points'}
                      direction={sortBy === 'points' ? sortDirection : sortDefaultDirection['points']}
                      onClick={() => handleSort('points')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'points' ? 1 : 0.3 } }}
                    >
                      Points
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sortDirection={sortBy === 'endorsements' ? sortDirection : false}>
                  <Tooltip title="Sort by endorsements" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'endorsements'}
                      direction={sortBy === 'endorsements' ? sortDirection : sortDefaultDirection['endorsements']}
                      onClick={() => handleSort('endorsements')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'endorsements' ? 1 : 0.3 } }}
                    >
                      Endorsements
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell sortDirection={sortBy === 'last_checked' ? sortDirection : false}>
                  <Tooltip title="Sort by last check date" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'last_checked'}
                      direction={sortBy === 'last_checked' ? sortDirection : sortDefaultDirection['last_checked']}
                      onClick={() => handleSort('last_checked')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'last_checked' ? 1 : 0.3 } }}
                    >
                      Last Check Date
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell sortDirection={sortBy === 'photocard_expiry' ? sortDirection : false}>
                  <Tooltip title="Sort by photocard expiry" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'photocard_expiry'}
                      direction={sortBy === 'photocard_expiry' ? sortDirection : sortDefaultDirection['photocard_expiry']}
                      onClick={() => handleSort('photocard_expiry')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'photocard_expiry' ? 1 : 0.3 } }}
                    >
                      Photocard Expiry
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell sortDirection={sortBy === 'can_drive_until' ? sortDirection : false}>
                  <Tooltip title="Sort by licence expiry" placement="top" arrow>
                    <TableSortLabel
                      active={sortBy === 'can_drive_until'}
                      direction={sortBy === 'can_drive_until' ? sortDirection : sortDefaultDirection['can_drive_until']}
                      onClick={() => handleSort('can_drive_until')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortBy === 'can_drive_until' ? 1 : 0.3 } }}
                    >
                      Can Drive Until
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStaff.map((staff) => (
                <TableRow key={staff.id} hover>
                  <TableCell>
                    {staff.first_name} {staff.last_name}
                  </TableCell>
                  <TableCell>{staff.driving_license_number}</TableCell>
                  <TableCell align="center">
                    {staff.date_of_birth ? calculateAge(staff.date_of_birth) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={staff.status || 'Unknown'}
                      color={getStatusColor(staff.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={staff.points_total || 0}
                      color={
                        (staff.points_total || 0) >= 6
                          ? 'error'
                          : (staff.points_total || 0) >= 3
                          ? 'warning'
                          : 'success'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={formatEndorsementSummary(staff.endorsement_summary)} placement="top" arrow>
                      <Chip
                        label={staff.endorsement_count || 0}
                        color={(staff.endorsement_count || 0) > 0 ? 'warning' : 'default'}
                        size="small"
                        variant={(staff.endorsement_count || 0) > 0 ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {staff.last_checked
                      ? new Date(staff.last_checked).toLocaleDateString('en-GB')
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    {staff.photocard_expiry ? (
                      <Box component="span" sx={getDateWarningSx(staff.photocard_expiry)}>
                        {new Date(staff.photocard_expiry).toLocaleDateString('en-GB')}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {staff.entitlement_dates_mixed
                      ? <Tooltip title="Vehicle categories have different expiry dates — view details for breakdown">
                          <span style={{ color: '#FF9800', fontStyle: 'italic', cursor: 'default' }}>See Details</span>
                        </Tooltip>
                      : staff.common_entitlement_expiry
                        ? <Box component="span" sx={getDateWarningSx(staff.common_entitlement_expiry)}>
                            {new Date(staff.common_entitlement_expiry).toLocaleDateString('en-GB')}
                          </Box>
                        : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewDetails(staff.id)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Run DVLA Check">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCheckLicense(staff.id)}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Archive">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleArchiveStaff(staff.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          )}
        </>
      )}

      {/* DVLA Check Confirmation Dialog */}
      <Dialog 
        open={checkConfirmDialogOpen} 
        onClose={() => setCheckConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">DVLA License Check Confirmation</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setCheckConfirmDialogOpen(false)}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Compliance Requirement
            </Typography>
            <Typography variant="body2">
              Before performing a DVLA license check, you must confirm that you have received the required signed consent form(s).
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please review the DVLA consent form:
            </Typography>
            <Link
              href={process.env.PUBLIC_URL + '/assets/DVLAConsent.pdf'}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center',
                textDecoration: 'none',
                mt: 1,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="primary" fontWeight="medium">
                Download DVLAConsent.pdf
              </Typography>
            </Link>
          </Box>

          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            backgroundColor: 'background.paper'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentConfirmed}
                  onChange={(e) => setConsentConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  We confirm we are in receipt of signed D906/ADD form(s) for the licence(s) we are submitting
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setCheckConfirmDialogOpen(false);
              setConsentConfirmed(false);
              setPendingStaffId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCheckLicense}
            variant="contained"
            disabled={!consentConfirmed}
          >
            Confirm & Run Check
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => !addingStaff && setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Add New Staff Member</Typography>
            <IconButton
              aria-label="close"
              onClick={() => !addingStaff && setAddDialogOpen(false)}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Compliance Requirement
            </Typography>
            <Typography variant="body2">
              Before adding a staff member and performing a DVLA check, you must confirm that you have received the required signed consent form(s).
            </Typography>
          </Alert>

          <TextField
            autoFocus
            margin="dense"
            label="Driving License Number"
            fullWidth
            value={newLicenseNumber}
            onChange={(e) => setNewLicenseNumber(e.target.value.toUpperCase().replace(/\s/g, ''))}
            placeholder="16-character license number"
            helperText="Enter the 16-character UK driving license number (e.g., SXJFD602174FN9NW)"
            disabled={addingStaff}
            inputProps={{ maxLength: 16 }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please review the DVLA consent form:
            </Typography>
            <Link
              href={process.env.PUBLIC_URL + '/assets/DVLAConsent.pdf'}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center',
                textDecoration: 'none',
                mt: 1,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="primary" fontWeight="medium">
                Download DVLAConsent.pdf
              </Typography>
            </Link>
          </Box>

          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            backgroundColor: 'background.paper'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={addStaffConsentConfirmed}
                  onChange={(e) => setAddStaffConsentConfirmed(e.target.checked)}
                  color="primary"
                  disabled={addingStaff}
                />
              }
              label={
                <Typography variant="body2">
                  We confirm we are in receipt of signed D906/ADD form(s) for the licence(s) we are submitting
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setAddDialogOpen(false);
              setNewLicenseNumber('');
              setAddStaffConsentConfirmed(false);
            }} 
            disabled={addingStaff}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddStaff}
            variant="contained"
            disabled={addingStaff || newLicenseNumber.length !== 16 || !addStaffConsentConfirmed}
            startIcon={addingStaff ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingStaff ? 'Adding...' : 'Add Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Licenses Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Import Driving Licenses</Typography>
            <IconButton
              aria-label="close"
              onClick={() => {
                if (importRunning) return;
                setImportDialogOpen(false);
                setImportLicenseText('');
                setImportConsentConfirmed(false);
                setImportResults([]);
              }}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Compliance Requirement
            </Typography>
            <Typography variant="body2">
              Before importing and running DVLA checks, you must confirm that you have received the required signed consent form(s) for all licence holders.
            </Typography>
          </Alert>

          <input
            type="file"
            ref={importFileInputRef}
            accept=".txt,.csv"
            onChange={handleImportFileChange}
            style={{ display: 'none' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => importFileInputRef.current?.click()}
              disabled={importRunning}
            >
              Upload .txt or .csv
            </Button>
            <Typography variant="body2" color="text.secondary">
              File: one license per line (.txt) or one per row, first column (.csv)
            </Typography>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label="Driving license numbers (one per line)"
            fullWidth
            multiline
            minRows={6}
            maxRows={12}
            value={importLicenseText}
            onChange={(e) => setImportLicenseText(e.target.value.toUpperCase())}
            placeholder={'SXJFD602174FN9NW\nHARTW123456J99AA'}
            helperText="Paste one 16-character UK driving license number per line. Spaces are removed automatically. Duplicates and invalid lines are skipped."
            disabled={importRunning}
            sx={{ mb: 2 }}
          />

          {importRunning && importProgress.total > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Processing {importProgress.current} of {importProgress.total}
                </Typography>
                <Typography variant="body2" fontWeight="medium" color="primary">
                  {Math.round((importProgress.current / importProgress.total) * 100)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(importProgress.current / importProgress.total) * 100}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          )}

          {importResults.length > 0 && !importRunning && (
            <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>Import results</Typography>
              {importResults.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Typography variant="body2" fontFamily="monospace">{r.license}</Typography>
                  <Chip
                    size="small"
                    label={r.status === 'added' ? 'Added' : r.status === 'duplicate' ? 'Already in system' : 'Failed'}
                    color={r.status === 'added' ? 'success' : r.status === 'duplicate' ? 'default' : 'error'}
                  />
                  {r.message && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                      {r.message}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please review the DVLA consent form:
            </Typography>
            <Link
              href={process.env.PUBLIC_URL + '/assets/DVLAConsent.pdf'}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                textDecoration: 'none',
                mt: 1,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="primary" fontWeight="medium">
                Download DVLAConsent.pdf
              </Typography>
            </Link>
          </Box>

          <Box sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            backgroundColor: 'background.paper'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={importConsentConfirmed}
                  onChange={(e) => setImportConsentConfirmed(e.target.checked)}
                  color="primary"
                  disabled={importRunning}
                />
              }
              label={
                <Typography variant="body2">
                  We confirm we are in receipt of signed D906/ADD form(s) for the licence(s) we are submitting
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setImportDialogOpen(false);
              setImportLicenseText('');
              setImportConsentConfirmed(false);
              setImportResults([]);
            }}
            disabled={importRunning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={
              importRunning ||
              parseImportLicenses(importLicenseText).length === 0 ||
              !importConsentConfirmed
            }
            startIcon={importRunning ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {importRunning ? 'Importing…' : 'Run checks & add to staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" component="span">Staff Member Details</Typography>
              {selectedStaff && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedStaff.first_name} {selectedStaff.last_name}
                </Typography>
              )}
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setDetailDialogOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : selectedStaff ? (
            <Box>
              {/* Basic Information */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Full Name</Typography>
                      <Typography variant="body1">
                        {selectedStaff.first_name} {selectedStaff.last_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                      <Typography variant="body1">
                        {selectedStaff.date_of_birth 
                          ? new Date(selectedStaff.date_of_birth).toLocaleDateString('en-GB')
                          : '-'}
                        {selectedStaff.date_of_birth && (
                          <span style={{ marginLeft: 8, color: '#666' }}>
                            (Age: {calculateAge(selectedStaff.date_of_birth)})
                          </span>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">License Number</Typography>
                      <Typography variant="body1">{selectedStaff.driving_license_number}</Typography>
                    </Grid>
                    {selectedStaff.latestCheck && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Gender</Typography>
                          <Typography variant="body1">
                            {selectedStaff.latestCheck.driver_gender || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Address</Typography>
                          <Typography variant="body1">
                            {[
                              selectedStaff.latestCheck.address_line1,
                              selectedStaff.latestCheck.address_line2,
                              selectedStaff.latestCheck.address_line3,
                              selectedStaff.latestCheck.address_line4,
                              selectedStaff.latestCheck.address_line5,
                              selectedStaff.latestCheck.address_postcode
                            ].filter(Boolean).join(', ') || '-'}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* License Information */}
              {selectedStaff.latestCheck && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>License Information</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Chip
                          label={selectedStaff.latestCheck.license_status || 'Unknown'}
                          color={getStatusColor(selectedStaff.latestCheck.license_status)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">License Type</Typography>
                        <Typography variant="body1">
                          {selectedStaff.latestCheck.license_type || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Issue Number</Typography>
                        <Typography variant="body1">
                          {selectedStaff.latestCheck.license_issue_number || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Valid From</Typography>
                        <Typography variant="body1">
                          {selectedStaff.latestCheck.license_valid_from
                            ? new Date(selectedStaff.latestCheck.license_valid_from).toLocaleDateString('en-GB')
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Photocard Expires</Typography>
                        <Typography variant="body1">
                          {(selectedStaff.latestCheck.license_expiry_date || selectedStaff.latestCheck.license_valid_until)
                            ? new Date(selectedStaff.latestCheck.license_expiry_date || selectedStaff.latestCheck.license_valid_until).toLocaleDateString('en-GB')
                            : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          It is an offence not to update your photo — please update your photo
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Driving Valid Until</Typography>
                        {(() => {
                          const cats = selectedStaff.vehicleCategories || [];
                          const dates: string[] = cats.map((c: any) => c.expiry_date as string).filter((d: string | null | undefined): d is string => !!d);
                          const uniqueDates = Array.from(new Set(dates.map(d => new Date(d).toISOString().split('T')[0])));
                          if (cats.length === 0 || dates.length === 0) {
                            return <Typography variant="body1">-</Typography>;
                          } else if (uniqueDates.length === 1) {
                            return <Typography variant="body1">{new Date(dates[0] as string).toLocaleDateString('en-GB')}</Typography>;
                          } else {
                            return (
                              <Tooltip title="Vehicle categories have different expiry dates — see Vehicle Entitlements below">
                                <Typography variant="body1" sx={{ color: '#FF9800', fontStyle: 'italic', cursor: 'default' }}>See Details ↓</Typography>
                              </Tooltip>
                            );
                          }
                        })()}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Points Total</Typography>
                        <Chip
                          label={selectedStaff.latestCheck.points_total || 0}
                          color={
                            (selectedStaff.latestCheck.points_total || 0) >= 6
                              ? 'error'
                              : (selectedStaff.latestCheck.points_total || 0) >= 3
                              ? 'warning'
                              : 'success'
                          }
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Last Checked</Typography>
                        <Typography variant="body1">
                          {selectedStaff.latestCheck.created_at
                            ? new Date(selectedStaff.latestCheck.created_at).toLocaleString('en-GB')
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Vehicle Categories / Entitlements */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Vehicle Entitlements</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    What this driver is licensed to drive
                  </Typography>
                  {selectedStaff.vehicleCategories && Array.isArray(selectedStaff.vehicleCategories) && selectedStaff.vehicleCategories.length > 0 ? (
                    selectedStaff.vehicleCategories.map((category: any, index: number) => (
                      <Accordion key={category.id || index} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {category.category_code || `Category ${index + 1}`}
                            </Typography>
                            <Chip
                              label={category.category_type || 'Full'}
                              size="small"
                              color={category.category_type === 'Full' ? 'success' : 'warning'}
                            />
                            {category.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                {category.description}
                              </Typography>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            {category.legal_literal && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>Legal Description</Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {category.legal_literal}
                                </Typography>
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">From Date</Typography>
                              <Typography variant="body1">
                                {category.from_date
                                  ? new Date(category.from_date).toLocaleDateString('en-GB')
                                  : '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Expiry Date</Typography>
                              <Typography variant="body1">
                                {category.expiry_date
                                  ? new Date(category.expiry_date).toLocaleDateString('en-GB')
                                  : 'No expiry'}
                              </Typography>
                            </Grid>
                            {category.restrictions && Array.isArray(category.restrictions) && category.restrictions.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Restrictions
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {category.restrictions.map((restriction: any, rIndex: number) => (
                                    <Chip
                                      key={rIndex}
                                      label={`${restriction.restriction_code || ''}: ${restriction.restriction_literal || ''}`}
                                      size="small"
                                      color="warning"
                                    />
                                  ))}
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    <Alert severity="info">
                      {selectedStaff.latestCheck 
                        ? 'No vehicle entitlements found in the latest check. This may indicate the DVLA check did not return entitlement information.'
                        : 'No vehicle entitlements found. Please run a DVLA check first to retrieve entitlement information.'}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Endorsements */}
              {selectedStaff.endorsements && selectedStaff.endorsements.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Endorsements</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Code</TableCell>
                            <TableCell>Points</TableCell>
                            <TableCell>Offense Date</TableCell>
                            <TableCell>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedStaff.endorsements.map((endorsement: any, index: number) => (
                            <TableRow key={endorsement.id || index}>
                              <TableCell>{endorsement.endorsement_code || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={endorsement.points || 0}
                                  size="small"
                                  color={endorsement.points >= 6 ? 'error' : 'warning'}
                                />
                              </TableCell>
                              <TableCell>
                                {endorsement.offense_date
                                  ? new Date(endorsement.offense_date).toLocaleDateString('en-GB')
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {endorsement.offense_legal_literal || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Check History */}
              {selectedStaff.licenseChecks && selectedStaff.licenseChecks.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Check History</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {selectedStaff.licenseChecks.length} check(s) performed
                    </Typography>
                    {selectedStaff.licenseChecks.slice(0, 5).map((check: any, index: number) => (
                      <Box key={check.id || index} sx={{ mb: 2, pb: 2, borderBottom: index < 4 ? '1px solid #eee' : 'none' }}>
                        <Typography variant="subtitle2">
                          {new Date(check.created_at).toLocaleString('en-GB')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip
                            label={check.license_status || 'Unknown'}
                            size="small"
                            color={getStatusColor(check.license_status)}
                          />
                          <Chip
                            label={`${check.points_total || 0} points`}
                            size="small"
                            color={
                              (check.points_total || 0) >= 6
                                ? 'error'
                                : (check.points_total || 0) >= 3
                                ? 'warning'
                                : 'default'
                            }
                          />
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Alert severity="info">No details available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* License Check Request Dialog */}
      <DVLALicenseRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        onSuccess={fetchStaff}
      />

      {/* Cancel Request Confirmation Dialog */}
      <Dialog open={cancelConfirmDialogOpen} onClose={() => setCancelConfirmDialogOpen(false)}>
        <DialogTitle>Cancel License Check Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this license check request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmDialogOpen(false)}>No, Keep It</Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            Yes, Cancel Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DVLAStaffTable;

